import { formatDate } from '@angular/common';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import {
  ConsumptionType,
  MaintenanceFrequency,
  MaintenanceType,
  PropertyEventType,
  PropertyMaintenance
} from '@eemo/api-client';
import moment from 'moment';

export type FinancialType = 'GLOBAL' | 'ENERGY' | 'MAINTENANCE' | 'WORK' | 'CHARGE';
export const FinancialType = {
  GLOBAL: 'GLOBAL' as FinancialType,
  ENERGY: 'ENERGY' as FinancialType,
  MAINTENANCE: 'MAINTENANCE' as FinancialType,
  WORK: 'WORK' as FinancialType,
  CHARGE: 'CHARGE' as FinancialType
};

@Injectable({ providedIn: 'root' })
export class TrackingHelper {
  constructor(@Inject(LOCALE_ID) public locale: string) {}

  public static getConsumptionTypesInOrder(): ConsumptionType[] {
    // set manually to manage orders (static used for sort function)
    return [
      ConsumptionType.ELECTRICITY,
      ConsumptionType.GAS,
      ConsumptionType.WATER,
      ConsumptionType.FUEL,
      ConsumptionType.WOOD
    ];
  }

  getPropertyEventTypes(): PropertyEventType[] {
    return Object.values(PropertyEventType);
  }

  getUpdatablePropertyEventTypes(): PropertyEventType[] {
    const types = this.getPropertyEventTypes();
    return types.filter(
      (type) =>
        ![
          PropertyEventType.WORK,
          PropertyEventType.MAINTENANCE,
          PropertyEventType.EQUIPMENT,
          PropertyEventType.CERTIFICATION,
          PropertyEventType.CERTIFICATION_RENEW,
          PropertyEventType.OWNERSHIP_TRANSFER
        ].includes(type)
    );
  }

  getNonEEMOEventTypes(): PropertyEventType[] {
    const types = this.getPropertyEventTypes();
    const eemoTypes = this.getEEMOEventTypes();
    return types.filter((type) => !eemoTypes.includes(type));
  }

  getEEMOEventTypes(): PropertyEventType[] {
    return [
      PropertyEventType.CREATION,
      PropertyEventType.CERTIFICATION,
      PropertyEventType.CERTIFICATION_RENEW,
      PropertyEventType.OWNERSHIP_TRANSFER
    ];
  }

  isEEMOEventType(type: PropertyEventType): boolean {
    return this.getEEMOEventTypes().includes(type);
  }

  getPropertyEventTypeName(type: PropertyEventType): string {
    switch (type) {
      case 'BUILDING':
        return 'Construction';
      case 'PURCHASE':
        return 'Achat';
      case 'EQUIPMENT':
        return 'Équipements';
      case 'DISASTER':
        return 'Sinistre';
      case 'ENVIRONMENT':
        return 'Environnement';
      case 'WORK':
        return 'Travaux';
      case 'MAINTENANCE':
        return 'Maintenance';
      case 'CREATION':
        return 'Enregistrement dans EEMO';
      case 'CERTIFICATION':
        return 'Certification EEMO';
      case 'CERTIFICATION_RENEW':
        return 'Renouvellement Certification EEMO';
      case 'OWNERSHIP_TRANSFER':
        return 'Transfert de propriété dans EEMO';
      default:
        return 'Type inconnu';
    }
  }

  getConsumptionTypes(): ConsumptionType[] {
    return TrackingHelper.getConsumptionTypesInOrder();
  }

  getConsumptionTypeName(type: ConsumptionType) {
    switch (type) {
      case ConsumptionType.ELECTRICITY:
        return 'Électricité';
      case ConsumptionType.GAS:
        return 'Gaz';
      case ConsumptionType.WATER:
        return 'Eau';
      case ConsumptionType.FUEL:
        return 'Fuel';
      case ConsumptionType.WOOD:
        return 'Bois';
      default:
        return 'Type inconnu';
    }
  }

  getConsumptionTypeUnit(type: ConsumptionType) {
    switch (type) {
      case ConsumptionType.ELECTRICITY:
        return 'kWh';
      case ConsumptionType.GAS:
        return 'kWh';
      case ConsumptionType.WATER:
        return 'm3';
      case ConsumptionType.FUEL:
        return 'L';
      case ConsumptionType.WOOD:
        return 'stères';
      default:
        return 'Type inconnu';
    }
  }

  getConsumptionLineColor(type: ConsumptionType) {
    switch (type) {
      case ConsumptionType.ELECTRICITY:
        return '#3366cc';
      case ConsumptionType.GAS:
        return '#ff9900';
      case ConsumptionType.WATER:
        return '#0099C6';
      case ConsumptionType.FUEL:
        return '#dc3912';
      case ConsumptionType.WOOD:
        return '#8B4513';
      default:
        return '#000000';
    }
  }

  getConsumptionPriceLineColor(): string {
    return '#109618';
  }

  getFinancialTypes(): string[] {
    return Object.keys(FinancialType);
  }

  getFinancialTypeName(type: FinancialType) {
    switch (type) {
      case FinancialType.GLOBAL:
        return 'Global';
      case FinancialType.ENERGY:
        return 'Énergie & Eau';
      case FinancialType.MAINTENANCE:
        return 'Entretien';
      case FinancialType.WORK:
        return 'Travaux';
      case FinancialType.CHARGE:
        return 'Impôts & Charges';
      default:
        return 'Type inconnu';
    }
  }

  getFinancialReportLineColor(type: FinancialType) {
    switch (type) {
      case 'GLOBAL':
        return '#3366cc';
      case 'ENERGY':
        return '#109618';
      case 'MAINTENANCE':
        return '#ff9900';
      case 'WORK':
        return '#dc3912';
      case 'CHARGE':
        return '#8B4513';
      default:
        return '#000000';
    }
  }

  getMonthsNumbers(): string[] {
    return ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
  }

  getMonthName(monthNumber: string) {
    switch (monthNumber) {
      case '01':
        return 'Janvier';
      case '02':
        return 'Février';
      case '03':
        return 'Mars';
      case '04':
        return 'Avril';
      case '05':
        return 'Mai';
      case '06':
        return 'Juin';
      case '07':
        return 'Juillet';
      case '08':
        return 'Août';
      case '09':
        return 'Septembre';
      case '10':
        return 'Octobre';
      case '11':
        return 'Novembre';
      case '12':
        return 'Décembre';
      default:
        return 'Mois inconnu';
    }
  }

  getCurrentYear(): string {
    return formatDate(Date.now(), 'yyyy', this.locale);
  }

  getMaintenanceTypes(): string[] {
    return Object.values(MaintenanceType);
  }

  getMaintenanceTypeName(type: MaintenanceType): string {
    switch (type) {
      case 'ENERGY_EQUIPMENT':
        return 'Équipement énergétique';
      case 'SANITATON_EQUIPMENT':
        return "Équipement d'assainissement";
      case 'BUILDING':
        return 'Bâtiment';
      case 'INSIDE':
        return 'Intérieur';
      case 'OUTSIDE':
        return 'Extérieur';
      case 'OTHER':
        return 'Autre';
      default:
        return 'Type inconnu';
    }
  }

  getMaintenanceFrequencies(): string[] {
    return Object.keys(MaintenanceFrequency);
  }

  getMaintenanceFrequencyName(type: MaintenanceFrequency): string {
    switch (type) {
      case MaintenanceFrequency.YEAR:
        return '1 an';
      case MaintenanceFrequency.YEARS2:
        return '2 ans';
      case MaintenanceFrequency.YEARS5:
        return '5 ans';
      case MaintenanceFrequency.YEARS10:
        return '10 ans';
      default:
        return 'Fréquence inconnue';
    }
  }

  isMaintenanceToDo(maintenance: PropertyMaintenance): boolean {
    const dateNow = moment(new Date());
    const dateToDo = moment(maintenance.toDoDate);
    return dateToDo.isSameOrBefore(dateNow) || Math.abs(dateToDo.diff(dateNow, 'months')) === 0;
  }
}
